import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, NavLink, Link, useLocation, matchPath } from 'react-router-dom';
import { SignedIn, SignedOut, SignInButton, useAuth, useClerk, useUser } from '@clerk/clerk-react';
import { Calendar, BarChart3, Settings as SettingsIcon, BookOpen, GraduationCap, Sun, Moon, Flame, Trophy, MoreHorizontal, X, Target, Calculator, Home as HomeIcon, ShoppingBag, Users, User, ChevronDown, ShieldCheck, LogOut, ExternalLink, Layers } from 'lucide-react';
import { useLiveQuery } from 'dexie-react-hooks';

import { HelmetProvider } from 'react-helmet-async';
import { ToastProvider } from './components/Toast';
import { applyAccentColor } from './utils/themeUtils';
import { db } from './db/db';

function lazyWithRetry<T extends React.ComponentType<any>>(
  componentImport: () => Promise<{ default: T }>
) {
  return React.lazy(async () => {
    const pageHasBeenRefreshed = sessionStorage.getItem('chunk_reload_attempts');
    try {
      const component = await componentImport();
      sessionStorage.removeItem('chunk_reload_attempts');
      return component;
    } catch (error: any) {
      const errorMsg = error?.message || '';
      const isChunkError =
        errorMsg.includes('expected expression') ||
        errorMsg.includes("got '<'") ||
        errorMsg.includes('Failed to fetch dynamically imported module') ||
        errorMsg.includes('Importing a module script failed');

      if (isChunkError || !pageHasBeenRefreshed) {
        sessionStorage.setItem('chunk_reload_attempts', '1');
        window.location.reload();
      }
      throw error;
    }
  });
}

const Home = lazyWithRetry(() => import('./pages/Home'));
import Landing, { LandingHeader, LandingFooter } from './pages/Landing';
const Dashboard = lazyWithRetry(() => import('./pages/Dashboard'));

function MarketingLayout({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen flex flex-col bg-slate-50/50 dark:bg-slate-950">
      <LandingHeader />
      <main className="flex-1">
        {children}
      </main>
      <LandingFooter />
    </div>
  );
}
const NewPlan = lazyWithRetry(() => import('./pages/NewPlan'));
const PlanDetail = lazyWithRetry(() => import('./pages/PlanDetail'));
const Settings = lazyWithRetry(() => import('./pages/Settings'));
const Notebook = lazyWithRetry(() => import('./pages/Notebook'));
const Teachers = lazyWithRetry(() => import('./pages/Teachers'));
const Profile = lazyWithRetry(() => import('./pages/Profile'));
const Stats = lazyWithRetry(() => import('./pages/Stats'));
const SharedPlan = lazyWithRetry(() => import('./pages/SharedPlan'));
const Templates = lazyWithRetry(() => import('./pages/Templates'));
const StudyDesk = lazyWithRetry(() => import('./pages/StudyDesk'));
const Flashcards = lazyWithRetry(() => import('./pages/Flashcards'));
const VideoPlayer = lazyWithRetry(() => import('./pages/VideoPlayer'));
const Biz = lazyWithRetry(() => import('./pages/Biz'));
const Iletisim = lazyWithRetry(() => import('./pages/Iletisim'));
const Gizlilik = lazyWithRetry(() => import('./pages/Gizlilik'));
const KullanimKosullari = lazyWithRetry(() => import('./pages/KullanimKosullari'));
const Blog = lazyWithRetry(() => import('./pages/Blog'));
const BlogPost = lazyWithRetry(() => import('./pages/BlogPost'));
const OgrenIndex = lazyWithRetry(() => import('./pages/OgrenIndex'));
const OgrenDers = lazyWithRetry(() => import('./pages/OgrenDers'));
const OgrenKonu = lazyWithRetry(() => import('./pages/OgrenKonu'));
const AdminPanel = lazyWithRetry(() => import('./pages/AdminPanel'));
const EmojiSet = lazyWithRetry(() => import('./pages/EmojiSet'));
const AdminTeacherInvite = lazyWithRetry(() => import('./pages/AdminTeacherInvite'));
const ScoreCalculator = lazyWithRetry(() => import('./pages/ScoreCalculator'));
const NotFound = lazyWithRetry(() => import('./pages/NotFound'));
import GlobalSearchModal from './components/GlobalSearchModal';
import SyncManager from './components/SyncManager';
import { AccessibilityModal } from './components/AccessibilityModal';
import { AccessibilityIcon } from './components/AccessibilityIcon';
import { getA11yFabVisible } from './utils/accessibility';
import { useSettings } from './hooks/useSettings';
import { checkStreakOnLogin } from './utils/streakUtils';
import { checkAndTriggerStreakReminder } from './utils/notificationCheck';
import { getExamDayDiff, getExamCountdownLabel, getLocalDateString } from './utils/dateUtils';
import ExamNoticeModal from './components/ExamNoticeModal';
import ErrorBoundary from './components/ErrorBoundary';

const logo = '/logo_miyo.webp';
const baslatLogo = '/baslat.webp';

// ─── Sidebar Nav Links (desktop & drawer) ──────────────────────────────────
// Küçük kırmızı sayı rozeti — bugün tekrar edilmesi gereken bilgi kartı sayısı (gerçek, hesaplanan değer).
const NavBadge = ({ count }: { count: number }) => {
  if (!count || count <= 0) return null;
  return (
    <span className="ml-auto min-w-[18px] h-[18px] px-1 rounded-full bg-danger-500 text-white text-[10px] font-black flex items-center justify-center shrink-0">
      {count > 99 ? '99+' : count}
    </span>
  );
};

const StartBadge = () => (
  <span className="ml-auto px-2 py-0.5 rounded-full bg-accent-500 text-white text-[10px] font-black uppercase tracking-wider shrink-0">
    Başla
  </span>
);

const NavLinks = ({ orientation = 'vertical', dueFlashcardsCount = 0, studyPlansCount = 0 }: { orientation?: 'vertical' | 'horizontal'; dueFlashcardsCount?: number; studyPlansCount?: number }) => {
  const isVert = orientation === 'vertical';

  const linkClass = (isActive: boolean) => {
    const base = "flex items-center rounded-xl font-medium transition-all whitespace-nowrap shrink-0";
    const layout = isVert 
      ? "gap-3 px-3.5 py-2.5 text-sm" 
      : "gap-1.5 px-2.5 py-1.5 text-xs xl:text-sm";
    const color = isActive 
      ? "bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500 font-bold" 
      : "text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800/60 hover:text-slate-900 dark:hover:text-white";
    return `${base} ${layout} ${color}`;
  };

  const iconSize = isVert ? 18 : 15;

  if (isVert) {
    return (
      <nav className="flex flex-col gap-5 flex-1 overflow-y-auto scrollbar-hide py-1">
        {/* ANA MENÜ */}
        <div>
          <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-3.5 mb-1.5 select-none">
            ANA MENÜ
          </div>
          <div className="flex flex-col gap-1">
            <NavLink to="/anasayfa" className={({ isActive }) => linkClass(isActive)} end>
              <div className="flex items-center justify-center shrink-0">
                <HomeIcon size={iconSize} className="opacity-80" />
              </div>
              Anasayfa
            </NavLink>
            <NavLink to="/masa" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <ShoppingBag size={iconSize} className="opacity-80" />
              </div>
              Çalışma Masam
            </NavLink>
            <NavLink to="/planlar" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <Calendar size={iconSize} className="opacity-80" />
              </div>
              Planlarım
              {studyPlansCount === 0 && <StartBadge />}
            </NavLink>
            <NavLink to="/defter" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <BookOpen size={iconSize} className="opacity-80" />
              </div>
              Defterim
            </NavLink>

          </div>
        </div>

        {/* ARAÇLAR */}
        <div>
          <div className="text-[10px] font-bold text-slate-400 dark:text-slate-500 uppercase tracking-widest px-3.5 mb-1.5 select-none">
            ARAÇLAR
          </div>
          <div className="flex flex-col gap-1">
            <NavLink to="/egitmenler" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <Users size={iconSize} className="opacity-80" />
              </div>
              Eğitmenlerim
            </NavLink>
            <NavLink to="/kartlar" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <Layers size={iconSize} className="opacity-80" />
              </div>
              <span>Kartlarım</span>
              <NavBadge count={dueFlashcardsCount} />
            </NavLink>
            <NavLink to="/puanhesaplama" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <Calculator size={iconSize} className="opacity-80" />
              </div>
              Puan Hesapla
            </NavLink>
            <NavLink to="/analiz" className={({ isActive }) => linkClass(isActive)}>
              <div className="flex items-center justify-center shrink-0">
                <BarChart3 size={iconSize} className="opacity-80" />
              </div>
              İstatistikler
            </NavLink>
            {/* Kardeş subdomainler — dofollow dış linkler */}
            <a
              href="https://soru.bitmiyo.com.tr"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass(false)}
            >
              <div className="flex items-center justify-center shrink-0">
                <ExternalLink size={iconSize} className="opacity-80" />
              </div>
              Soru Bankası
            </a>
            <a
              href="https://sinif.bitmiyo.com.tr"
              target="_blank"
              rel="noopener noreferrer"
              className={linkClass(false)}
            >
              <div className="flex items-center justify-center shrink-0">
                <GraduationCap size={iconSize} className="opacity-80" />
              </div>
              Bitmiyo Sınıf
            </a>
          </div>
        </div>
      </nav>
    );
  }

  return (
    <nav className="flex flex-row gap-1.5 lg:gap-3 xl:gap-4 items-center">
      <NavLink to="/anasayfa" className={({ isActive }) => linkClass(isActive)} end>
        <div className="flex items-center justify-center shrink-0">
          <HomeIcon size={iconSize} className="opacity-80" />
        </div>
        Anasayfa
      </NavLink>
      <NavLink to="/masa" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0">
          <ShoppingBag size={iconSize} className="opacity-80" />
        </div>
        Çalışma Masam
      </NavLink>
      <NavLink to="/planlar" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><Calendar size={iconSize} className="opacity-80" /></div>
        Planlarım
        {studyPlansCount === 0 && <StartBadge />}
      </NavLink>
      <NavLink to="/defter" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><BookOpen size={iconSize} className="opacity-80" /></div>
        Defterim
      </NavLink>

      <NavLink to="/egitmenler" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><Users size={iconSize} className="opacity-80" /></div>
        Eğitmenlerim
      </NavLink>
      <NavLink to="/kartlar" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><Layers size={iconSize} className="opacity-80" /></div>
        <span>Kartlarım</span>
        <NavBadge count={dueFlashcardsCount} />
      </NavLink>
      <NavLink to="/puanhesaplama" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><Calculator size={iconSize} className="opacity-80" /></div>
        Puan Hesapla
      </NavLink>
      <NavLink to="/analiz" className={({ isActive }) => linkClass(isActive)}>
        <div className="flex items-center justify-center shrink-0"><BarChart3 size={iconSize} className="opacity-80" /></div>
        İstatistikler
      </NavLink>
      {/* Kardeş subdomainler — dofollow dış linkler */}
      <a href="https://soru.bitmiyo.com.tr" target="_blank" rel="noopener noreferrer" className={linkClass(false)}>
        <div className="flex items-center justify-center shrink-0"><ExternalLink size={iconSize} className="opacity-80" /></div>
        Soru Bankası
      </a>
      <a href="https://sinif.bitmiyo.com.tr" target="_blank" rel="noopener noreferrer" className={linkClass(false)}>
        <div className="flex items-center justify-center shrink-0"><GraduationCap size={iconSize} className="opacity-80" /></div>
        Bitmiyo Sınıf
      </a>
    </nav>
  );
};

const Logo = () => (
  <div className="flex items-center gap-3 font-extrabold text-2xl text-slate-900 dark:text-white tracking-tighter italic drop-shadow-sm transition-colors duration-500">
    <div className="w-10 h-10 overflow-hidden flex items-center justify-center">
      <img src={logo} alt="Bitmiyo Logo" className="w-full h-full object-contain" />
    </div>
    <span>BİTMİYO</span>
  </div>
);

// ─── Profil Menüsü (navbar/sidebar birleşik "Profil" butonu) ────────────────
// Clerk'in kendi UserButton'ı yerine kullanılır: dağınık Ayarlar/Başarılar/
// İstatistikler linklerini tek bir hızlı-erişim menüsünde toplar; hesap
// yönetimi (Clerk) menü içinden "Hesabımı Yönet" ile açılır — iki farklı
// "profil" butonu aynı anda gösterilmiyor.
function ProfileMenu({ showName = false, direction = 'down', align = 'right' }: { showName?: boolean; direction?: 'up' | 'down'; align?: 'left' | 'right' }) {
  const { user } = useUser();
  const clerk = useClerk();
  const [open, setOpen] = useState(false);
  const ref = React.useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [open]);

  const menuItems = [
    { icon: User, label: 'Profilim', to: '/profil' },
    { icon: Trophy, label: 'Başarılarım', to: '/profil?tab=basarilar' },
    { icon: SettingsIcon, label: 'Ayarlar', to: '/ayarlar' },
  ];

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-2 py-1.5 rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        {user?.imageUrl ? (
          <img src={user.imageUrl} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
        ) : (
          <div className="w-8 h-8 rounded-full bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500 flex items-center justify-center shrink-0">
            <User size={16} />
          </div>
        )}
        {showName && (
          <span className="text-sm font-bold text-slate-700 dark:text-slate-300 truncate max-w-[120px]">
            {user?.firstName || 'Hesabım'}
          </span>
        )}
        <ChevronDown size={14} className={`text-slate-400 shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>

      {open && (
        <div
          className={`absolute w-60 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xl p-2 z-50 ${
            direction === 'up' ? 'bottom-full mb-2' : 'top-full mt-2'
          } ${align === 'left' ? 'left-0' : 'right-0'}`}
        >
          {menuItems.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              onClick={() => setOpen(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all"
            >
              <item.icon size={16} className="opacity-80" />
              {item.label}
            </Link>
          ))}
          <div className="my-1.5 border-t border-slate-100 dark:border-slate-800" />
          <button
            onClick={() => { setOpen(false); clerk.openUserProfile(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white transition-all"
          >
            <ShieldCheck size={16} className="opacity-80" />
            Hesabımı Yönet
          </button>
          <button
            onClick={() => { setOpen(false); clerk.signOut(); }}
            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium text-danger-500 hover:bg-danger-100/60 dark:hover:bg-danger-500/10 transition-all"
          >
            <LogOut size={16} className="opacity-80" />
            Çıkış Yap
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Mobile Bottom Navigation ────────────────────────────────────────────────
function MobileBottomNav({ onMoreClick, studyPlansCount = 0 }: {
  onMoreClick: () => void;
  studyPlansCount?: number;
}) {
  const location = useLocation();

  const isMoreActive = ['/defter', '/analiz', '/egitmenler', '/puanhesaplama', '/kartlar', '/ayarlar'].some(p =>
    location.pathname.startsWith(p)
  );

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-white/90 dark:bg-slate-900/90 backdrop-blur-xl border-t border-slate-200 dark:border-slate-700/60 safe-bottom shadow-2xl shadow-slate-900/10">
      <div className="flex items-stretch h-16">
        {/* Anasayfa */}
        <NavLink
          to="/anasayfa"
          end
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isActive ? 'text-accent-500' : 'text-slate-400 dark:text-slate-500'}`
          }
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>
          <span className="text-[10px] font-bold mt-1">Anasayfa</span>
        </NavLink>
        <NavLink to="/masa" className={({ isActive }) => `flex-1 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isActive ? 'text-accent-500' : 'text-slate-400 dark:text-slate-500'}`}>
          <BookOpen size={22} />
          <span className="text-[10px] font-bold mt-1">Masam</span>
        </NavLink>

        {/* Planlarım */}
        <NavLink
          to="/planlar"
          className={({ isActive }) =>
            `relative flex-1 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isActive ? 'text-accent-500' : 'text-slate-400 dark:text-slate-500'}`
          }
        >
          <div className="relative">
            <Calendar size={22} />
            {studyPlansCount === 0 && (
              <span className="absolute -top-1 -right-1.5 w-2.5 h-2.5 rounded-full bg-accent-500 border-2 border-white dark:border-slate-900" />
            )}
          </div>
          <span className="text-[10px] font-bold tracking-tight">Planlarım</span>
        </NavLink>

        {/* Başarılarım */}
        <NavLink
          to="/basarilar"
          className={({ isActive }) =>
            `flex-1 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isActive ? 'text-accent-500' : 'text-slate-400 dark:text-slate-500'}`
          }
        >
          <Trophy size={22} />
          <span className="text-[10px] font-bold tracking-tight">Başarılar</span>
        </NavLink>

        {/* Daha Fazla */}
        <button
          onClick={onMoreClick}
          className={`flex-1 flex flex-col items-center justify-center gap-0.5 transition-all active:scale-95 ${isMoreActive ? 'text-accent-500' : 'text-slate-400 dark:text-slate-500'}`}
        >
          <MoreHorizontal size={22} />
          <span className="text-[10px] font-bold tracking-tight">Daha Fazla</span>
        </button>
      </div>
    </nav>
  );
}

// ─── More Drawer (slide-up) ──────────────────────────────────────────────────
function MoreDrawer({ isOpen, onClose, toggleTheme, theme, streakCount, dueFlashcardsCount = 0 }: {
  isOpen: boolean;
  onClose: () => void;
  toggleTheme: () => void;
  theme?: string;
  streakCount?: number;
  dueFlashcardsCount?: number;
}) {
  const { isLoaded: isAuthLoaded } = useAuth();
  if (!isOpen) return null;

  return (
    <div className="md:hidden fixed inset-0 z-50 flex flex-col justify-end">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-slate-900/50 backdrop-blur-sm drawer-overlay"
        onClick={onClose}
      />
      {/* Drawer panel */}
      <div className="relative bg-white dark:bg-slate-900 rounded-t-3xl shadow-2xl drawer-content pb-safe">
        {/* Handle */}
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-slate-200 dark:bg-slate-700 rounded-full" />
        </div>

        <div className="flex items-center justify-between px-6 py-3 border-b border-slate-100 dark:border-slate-800">
          <span className="text-xs font-black text-slate-400 uppercase tracking-widest">Diğer Sayfalar</span>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-all"
          >
            <X size={18} />
          </button>
        </div>

        <nav className="p-4 flex flex-col gap-1">
          <NavLink
            to="/defter"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <BookOpen size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Defterim</div>
              <div className="text-xs text-slate-400 font-medium">Ders notları</div>
            </div>
          </NavLink>

          <NavLink
            to="/analiz"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <BarChart3 size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">İstatistikler</div>
              <div className="text-xs text-slate-400 font-medium">Gelişim analizi</div>
            </div>
          </NavLink>

          <NavLink
            to="/egitmenler"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <GraduationCap size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Eğitmenlerim</div>
              <div className="text-xs text-slate-400 font-medium">Hoca takibi</div>
            </div>
          </NavLink>

          <NavLink
            to="/puanhesaplama"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-accent-500">
              <Calculator size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Puan Hesapla</div>
              <div className="text-xs text-slate-400 font-medium">ÖSYM sınav puanı</div>
            </div>
          </NavLink>



          <NavLink
            to="/kartlar"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400 relative shrink-0">
              <Layers size={20} />
              {dueFlashcardsCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-[16px] px-1 rounded-full bg-danger-500 text-white text-[9px] font-black flex items-center justify-center">
                  {dueFlashcardsCount > 9 ? '9+' : dueFlashcardsCount}
                </span>
              )}
            </div>
            <div>
              <div className="font-bold text-sm">Kartlarım</div>
              <div className="text-xs text-slate-400 font-medium">
                {dueFlashcardsCount > 0 ? `${dueFlashcardsCount} kart tekrar bekliyor` : 'Bilgi kartları'}
              </div>
            </div>
          </NavLink>

          <a
            href="https://soru.bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <ExternalLink size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Soru Bankası</div>
              <div className="text-xs text-slate-400 font-medium">soru.bitmiyo.com.tr</div>
            </div>
          </a>

          <a
            href="https://sinif.bitmiyo.com.tr"
            target="_blank"
            rel="noopener noreferrer"
            onClick={onClose}
            className="flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800"
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <GraduationCap size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Bitmiyo Sınıf</div>
              <div className="text-xs text-slate-400 font-medium">sinif.bitmiyo.com.tr</div>
            </div>
          </a>

          <NavLink
            to="/ayarlar"
            onClick={onClose}
            className={({ isActive }) =>
              `flex items-center gap-4 px-4 py-3.5 rounded-2xl font-semibold transition-all ${isActive ? 'bg-accent-100 dark:bg-accent-500/10 text-accent-600 dark:text-accent-500' : 'text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-800'}`
            }
          >
            <div className="w-10 h-10 rounded-xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center text-slate-500 dark:text-slate-400">
              <SettingsIcon size={20} />
            </div>
            <div>
              <div className="font-bold text-sm">Ayarlar</div>
              <div className="text-xs text-slate-400 font-medium">Uygulama tercihleri</div>
            </div>
          </NavLink>
        </nav>

        {/* Streak + Theme Toggle */}
        <div className="mx-4 mb-4 p-4 bg-slate-50 dark:bg-slate-800/50 rounded-2xl flex items-center justify-between border border-slate-100 dark:border-slate-700">
          {(streakCount ?? 0) > 0 ? (
            <div className="flex items-center gap-2">
              <Flame size={18} className="text-game-500" />
              <span className="text-sm font-black text-game-500 dark:text-game-500">{streakCount} günlük seri!</span>
            </div>
          ) : (
            <span className="text-sm font-bold text-slate-400">Çalışmaya başla!</span>
          )}
          <button
            onClick={toggleTheme}
            className="flex items-center gap-2 px-3 py-2 rounded-xl font-bold text-sm transition-all text-slate-600 dark:text-slate-400 hover:bg-white dark:hover:bg-slate-700 border border-slate-200 dark:border-slate-600"
          >
            {theme === 'dark'
              ? <><Sun size={16} className="text-game-500" /> <span className="text-xs">Aydınlık</span></>
              : <><Moon size={16} /> <span className="text-xs">Karanlık</span></>
            }
          </button>
        </div>

        {/* Auth Section for Mobile Drawer */}
        <div className="mx-4 mb-safe pb-4">
          <div className="p-4 bg-slate-100 dark:bg-slate-800 rounded-2xl border border-border flex items-center justify-between">
            {!isAuthLoaded ? (
              <div className="h-[40px] w-full rounded-xl bg-slate-100 dark:bg-slate-800/30 animate-pulse" />
            ) : (
              <>
                <SignedIn>
                  <ProfileMenu showName direction="up" align="left" />
                </SignedIn>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="w-full py-2.5 bg-accent-500 text-white font-bold rounded-xl active:scale-95 transition-all text-sm">
                      Giriş Yap / Üye Ol
                    </button>
                  </SignInButton>
                </SignedOut>
              </>
            )}
          </div>
          
        </div>
      </div>
    </div>
  );
}

// ─── Main App ────────────────────────────────────────────────────────────────
// Uygulamada tanımlı tüm rotalar — sadece "bu adres 404 mü?" sorusuna cevap vermek için.
// Aşağıdaki <Routes> bloğuyla senkron tutulmalı.
const KNOWN_ROUTE_PATHS = [
  '/', '/anasayfa', '/masa', '/masa/izle/:taskId', '/planlar', '/yeni', '/plan/:id',
  '/analiz', '/ayarlar', '/profil', '/defter', '/basarilar', '/egitmenler', '/kartlar',
  '/paylas/:slug', '/sablonlar', '/biz', '/iletisim', '/gizlilik', '/kullanim-kosullari',
  '/blog', '/blog/:slug', '/puanhesaplama', '/puanhesaplama/:examSlug',
  '/ogren', '/ogren/:dersSlug', '/ogren/:dersSlug/:konuSlug',
  '/yonetim', '/yonetim/ogretmen-davet', '/emoji-set',
];

function AppShell() {
  const { settings, updateSettings, isLoading: isSettingsLoading } = useSettings();
  const { isLoaded: isAuthLoaded } = useAuth();
  const location = useLocation();
  const isImmersive = location.pathname.startsWith('/masa/izle/');
  // Herkese açık pazarlama/açılış sayfası ve yasal/hakkımızda sayfaları
  const publicMarketingRoutes = ['/', '/biz', '/iletisim', '/gizlilik', '/kullanim-kosullari', '/emoji-set'];
  const isLandingRoute =
    publicMarketingRoutes.includes(location.pathname) ||
    location.pathname === '/blog' ||
    location.pathname.startsWith('/blog/') ||
    location.pathname === '/ogren' ||
    location.pathname.startsWith('/ogren/');
  // 404 sayfası kendi minimal navbar'ını çizer — uygulama sidebar'ı/alt navı gizlenir.
  // DİKKAT: aşağıya yeni bir <Route> eklerken bu listeye de eklenmeli, yoksa o sayfa
  // 404 sanılıp app chrome'u olmadan render edilir.
  const isNotFoundRoute = !KNOWN_ROUTE_PATHS.some((p) => matchPath(p, location.pathname));
  const hideAppChrome = isImmersive || isLandingRoute || isNotFoundRoute;
  const isTopNavbar = settings?.navbarPosition === 'top';
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMoreDrawerOpen, setIsMoreDrawerOpen] = useState(false);
  const [isA11yOpen, setIsA11yOpen] = useState(false);
  const [fabVisible, setFabVisibleState] = useState(getA11yFabVisible());

  // FAB görünürlüğünü güncelle (Settings sayfasından tetiklenebilir)
  useEffect(() => {
    const onUpdate = () => setFabVisibleState(getA11yFabVisible());
    window.addEventListener('storage', onUpdate);
    window.addEventListener('a11y-fab-change', onUpdate);
    return () => {
      window.removeEventListener('storage', onUpdate);
      window.removeEventListener('a11y-fab-change', onUpdate);
    };
  }, []);

  const daysLeft = settings?.targetExamDate ? getExamDayDiff(settings.targetExamDate) : null;

  // Bugün tekrar edilmesi gereken bilgi kartı sayısı — gerçek, DB'den hesaplanan
  // bir değer (nextReviewDate <= bugün). Nav rozetinde gösterilir.
  const todayStrForCards = getLocalDateString();
  const dueFlashcardsCount = useLiveQuery(
    () => db.flashcards.where('nextReviewDate').belowOrEqual(todayStrForCards).count(),
    [todayStrForCards]
  ) ?? 0;

  // Henüz hiç plan yoksa "Planlarım" öğesinin yanında büyük "Başla" rozeti gösterilir.
  const studyPlansCount = useLiveQuery(() => db.studyPlans.count()) ?? 0;

  useEffect(() => {
    checkStreakOnLogin();
    checkAndTriggerStreakReminder();
    // Her saat başı hatırlatıcı bildirim kontrolünü çalıştır
    const interval = setInterval(checkAndTriggerStreakReminder, 3600000);
    return () => clearInterval(interval);
  }, []);

  const [examNotice, setExamNotice] = useState<{ title: string; message: string } | null>(null);

  useEffect(() => {
    if (!settings?.targetExamDate) return;
    const diff = getExamDayDiff(settings.targetExamDate);
    if (diff !== 1 && diff !== 0) return;

    const todayStr = getLocalDateString();
    const storageKey = `examNoticeShown_${settings.targetExamDate}_${todayStr}`;
    if (localStorage.getItem(storageKey)) return;

    setExamNotice(
      diff === 1
        ? {
            title: 'Yarın Sınav Günü!',
            message: 'Sınava Giriş Belgeni almayı unutma. Sınavda başarılar dileriz!',
          }
        : {
            title: 'Sınav Günü',
            message: 'Sınavda başarılar dileriz!',
          }
    );
    localStorage.setItem(storageKey, '1');
  }, [settings?.targetExamDate]);

  const toggleTheme = async () => {
    const newTheme = settings?.theme === 'dark' ? 'light' : 'dark';
    await updateSettings({ theme: newTheme });
  };

  useEffect(() => {
    if (settings?.theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }

    if (settings?.accentColor) {
      applyAccentColor(settings.accentColor);
    } else {
      applyAccentColor('teal');
    }
  }, [settings?.theme, settings?.accentColor]);

  // Handle global keyboard shortcuts
  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      // Ctrl + Q or Cmd + Q -> Toggle Global Search
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'q') {
        e.preventDefault();
        setIsSearchOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleGlobalKeyDown);
    return () => window.removeEventListener('keydown', handleGlobalKeyDown);
  }, []);

  return (
    <>
      {/* Ambient Background */}
      {!hideAppChrome && (
        <div className="fixed inset-0 pointer-events-none -z-10 bg-[#faf9f6] dark:bg-slate-950 transition-colors duration-500 overflow-hidden">
          <div className="absolute top-0 left-0 w-[500px] h-[500px] bg-brand-900/5 rounded-full blur-[120px] -translate-x-1/2 -translate-y-1/2 transition-colors duration-500"></div>
          <div className="absolute bottom-0 right-0 w-[600px] h-[600px] bg-accent-500/5 rounded-full blur-[120px] translate-x-1/3 translate-y-1/3 transition-colors duration-500"></div>
          <div className="absolute top-1/2 left-1/2 w-[800px] h-[800px] bg-brand-700/5 rounded-full blur-[150px] -translate-x-1/2 -translate-y-1/2 transition-colors duration-500"></div>
          <img
            src={baslatLogo}
            alt=""
            aria-hidden="true"
            style={{ transform: 'rotate(18deg)' }}
            className="absolute -bottom-24 right-4 w-[900px] h-[900px] object-contain opacity-[0.05] dark:opacity-[0.07] dark:invert transition-opacity duration-500 select-none"
          />
        </div>
      )}

      <div className={`min-h-screen flex text-slate-800 dark:text-slate-200 font-sans selection:bg-accent-100 selection:text-accent-600 dark:selection:bg-accent-500/20 dark:selection:text-accent-500 ${isTopNavbar ? 'flex-col' : 'flex-row'}`}>

        {!hideAppChrome && (isTopNavbar ? (
          /* Top Navbar — shown on all screen sizes */
          <header className="sticky top-0 z-50 w-full border-b border-slate-400/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl px-4 md:px-10 py-4 flex items-center justify-between shadow-lg shadow-slate-200/20 dark:shadow-slate-900/20 transition-colors duration-500">
            <div className="flex items-center gap-6">
              <Logo />
              {isSettingsLoading ? (
                <div className="hidden md:block w-36 h-[32px] rounded-lg bg-slate-100 dark:bg-slate-800/50 animate-pulse" />
              ) : settings?.targetExam && settings?.showExamCountdown !== false ? (
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 border border-accent-500/20 text-accent-600 dark:text-accent-500 font-bold text-xs uppercase tracking-wider">
                  <Target size={14} />
                  <span>
                    {settings.targetExam.replace('_', ' ')}
                    {daysLeft !== null && ` • ${getExamCountdownLabel(daysLeft)}`}
                  </span>
                </div>
              ) : null}
            </div>
            {/* Desktop nav links */}
            <div className="hidden md:flex items-center gap-4">
              <NavLinks orientation="horizontal" dueFlashcardsCount={dueFlashcardsCount} studyPlansCount={studyPlansCount} />
              <div className="ml-4 pl-4 border-l border-slate-300 dark:border-slate-700 flex items-center">
                <SignedIn>
                  <ProfileMenu direction="down" />
                </SignedIn>
                <SignedOut>
                  <SignInButton mode="modal">
                    <button className="px-4 py-2 bg-accent-500 hover:bg-accent-600 text-white font-bold rounded-xl transition-all text-sm shadow-md shadow-accent-500/20 active:scale-95">
                      Giriş Yap
                    </button>
                  </SignInButton>
                </SignedOut>
              </div>
            </div>
          </header>
        ) : (
          /* Sidebar — HIDDEN on mobile, shown on md+ */
          <aside className="hidden md:flex w-64 border-r border-slate-400/50 dark:border-slate-700/50 bg-white/60 dark:bg-slate-900/60 backdrop-blur-xl p-6 flex-col sticky top-0 h-screen shadow-2xl shadow-slate-200/30 dark:shadow-slate-900/30 transition-colors duration-500">
            <div className="mb-10">
              <Logo />
            </div>

            {isSettingsLoading ? (
              <div className="mb-6 h-[38px] w-full rounded-lg bg-slate-100 dark:bg-slate-800/50 animate-pulse" />
            ) : settings?.targetExam && settings?.showExamCountdown !== false ? (
              <div className="mb-6 flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-100 dark:bg-slate-800 border border-accent-500/20 text-accent-600 dark:text-accent-500 font-bold text-xs uppercase tracking-wider">
                <Target size={14} className="shrink-0" />
                <span className="line-clamp-1" title={settings.targetExam}>
                  {settings.targetExam.replace('_', ' ')}
                  {daysLeft !== null && ` • ${getExamCountdownLabel(daysLeft)}`}
                </span>
              </div>
            ) : null}

            <NavLinks orientation="vertical" dueFlashcardsCount={dueFlashcardsCount} studyPlansCount={studyPlansCount} />

            <div className="mt-auto pt-6 border-t border-slate-400 dark:border-slate-700 flex flex-col gap-1.5 transition-colors duration-500">
              {/* Streak Widget */}
              {isSettingsLoading ? (
                <div className="h-[52px] w-full mb-2 rounded-xl bg-slate-100 dark:bg-slate-800/50 animate-pulse" />
              ) : (settings?.streakCount ?? 0) > 0 ? (
                <div className="flex items-center gap-2.5 px-3 py-2 mb-2 rounded-xl bg-game-100 dark:bg-game-500/10 border border-game-500/20">
                  <Flame size={16} className="text-game-500 shrink-0" />
                  <div>
                    <span className="text-xs font-black text-game-500 dark:text-game-500">{settings?.streakCount} günlük seri!</span>
                    <p className="text-[10px] text-game-500/80 font-medium leading-tight">Devam et, durma</p>
                  </div>
                </div>
              ) : null}


              {/* Theme Toggle */}
              <button
                onClick={toggleTheme}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg font-medium transition-all text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white w-full"
                title={settings?.theme === 'dark' ? 'Aydınlık Moda Geç' : 'Karanlık Moda Geç'}
              >
                {settings?.theme === 'dark'
                  ? <Sun size={18} className="opacity-80 text-game-500" />
                  : <Moon size={18} className="opacity-80" />}
                {settings?.theme === 'dark' ? 'Aydınlık Mod' : 'Karanlık Mod'}
              </button>

              <div className="mt-2 pt-2 border-t border-slate-200 dark:border-slate-700/50 flex flex-col gap-2">
                {!isAuthLoaded ? (
                  <div className="h-[40px] w-full rounded-lg bg-slate-100 dark:bg-slate-800/50 animate-pulse" />
                ) : (
                  <>
                    <SignedIn>
                      <div className="px-1 py-1">
                        <ProfileMenu showName direction="up" align="left" />
                      </div>
                    </SignedIn>
                    <SignedOut>
                      <SignInButton mode="modal">
                        <button className="w-full px-3 py-2.5 bg-accent-500 hover:bg-accent-600 text-white font-bold rounded-lg transition-all text-sm shadow-md shadow-accent-500/20 active:scale-95">
                          Giriş Yap / Kayıt Ol
                        </button>
                      </SignInButton>
                    </SignedOut>
                  </>
                )}
              </div>
            </div>
          </aside>
        ))}

        {/* Main Content — add bottom padding on mobile for the nav bar */}
        <main className={`flex-1 flex flex-col ${hideAppChrome ? '' : 'pb-16 md:pb-0'}`}>
          <div className="flex-1">
            <ErrorBoundary>
              <React.Suspense fallback={<div className="p-8 text-center text-slate-500 animate-pulse">Yükleniyor...</div>}>
                <Routes>
                  <Route path="/" element={<Landing />} />
                  <Route path="/anasayfa" element={<Home />} />
                   <Route path="/masa" element={<StudyDesk />} />
                  <Route path="/masa/izle/:taskId" element={<VideoPlayer />} />
                  <Route path="/planlar" element={<Dashboard />} />
                  <Route path="/yeni" element={<NewPlan />} />
                  <Route path="/plan/:id" element={<PlanDetail />} />
                  <Route path="/analiz" element={<Stats />} />
                  <Route path="/ayarlar" element={<Settings />} />
                  <Route path="/profil" element={<Profile />} />
                  <Route path="/defter" element={<Notebook />} />
                  <Route path="/basarilar" element={<Navigate to="/profil?tab=basarilar" replace />} />
                  <Route path="/desk" element={<Navigate to="/masa" replace />} />
                  <Route path="/plans" element={<Navigate to="/planlar" replace />} />
                  <Route path="/stats" element={<Navigate to="/analiz" replace />} />
                  <Route path="/egitmenler" element={<Teachers />} />
                  <Route path="/kartlar" element={<Flashcards />} />
                  <Route path="/paylas/:slug" element={<SharedPlan />} />
                  <Route path="/sablonlar" element={<Templates />} />
                  <Route path="/biz" element={<MarketingLayout><Biz /></MarketingLayout>} />
                  <Route path="/iletisim" element={<MarketingLayout><Iletisim /></MarketingLayout>} />
                  <Route path="/gizlilik" element={<MarketingLayout><Gizlilik /></MarketingLayout>} />
                  <Route path="/kullanim-kosullari" element={<MarketingLayout><KullanimKosullari /></MarketingLayout>} />
                  <Route path="/blog" element={<MarketingLayout><Blog /></MarketingLayout>} />
                  <Route path="/blog/:slug" element={<MarketingLayout><BlogPost /></MarketingLayout>} />
                  <Route path="/ogren" element={<MarketingLayout><OgrenIndex /></MarketingLayout>} />
                  <Route path="/ogren/:dersSlug" element={<MarketingLayout><OgrenDers /></MarketingLayout>} />
                  <Route path="/ogren/:dersSlug/:konuSlug" element={<MarketingLayout><OgrenKonu /></MarketingLayout>} />
                  <Route path="/puanhesaplama/:examSlug?" element={<ScoreCalculator />} />
                  <Route path="/yonetim" element={<AdminPanel />} />
                  <Route path="/yonetim/ogretmen-davet" element={<AdminTeacherInvite />} />
                  <Route path="/emoji-set" element={<EmojiSet />} />
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </React.Suspense>
            </ErrorBoundary>
          </div>

          

        </main>
      </div>

      {/* Mobile Bottom Navigation */}
      {!hideAppChrome && (
        <MobileBottomNav
          onMoreClick={() => setIsMoreDrawerOpen(true)}
          studyPlansCount={studyPlansCount}
        />
      )}

      {/* More Drawer */}
      <MoreDrawer
        isOpen={isMoreDrawerOpen}
        onClose={() => setIsMoreDrawerOpen(false)}
        toggleTheme={toggleTheme}
        theme={settings?.theme}
        streakCount={settings?.streakCount}
        dueFlashcardsCount={dueFlashcardsCount}
      />

      <GlobalSearchModal
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      <ExamNoticeModal
        isOpen={examNotice !== null}
        onClose={() => setExamNotice(null)}
        title={examNotice?.title || ''}
        message={examNotice?.message || ''}
      />

      {/* Yüzen Erişilebilirlik Butonu — FAB visibility kontrollü */}
      {fabVisible && (
        <button
          type="button"
          onClick={() => setIsA11yOpen(true)}
          className="fixed bottom-20 right-5 md:bottom-5 z-40 w-12 h-12 bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-700 rounded-2xl flex items-center justify-center shadow-2xl transition-all hover:scale-105 active:scale-95 border border-slate-200 dark:border-slate-600"
          title="Erişilebilirlik Ayarlarını Aç"
        >
          <AccessibilityIcon size={24} />
        </button>
      )}

      <AccessibilityModal isOpen={isA11yOpen} onClose={() => setIsA11yOpen(false)} />

      <SyncManager />
    </>
  );
}

function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function App() {
  return (
    <HelmetProvider>
      <ToastProvider>
        <Router>
          <ScrollToTop />
          <AppShell />
        </Router>
      </ToastProvider>
    </HelmetProvider>
  );
}

export default App;
